<template>
  <transition name="modal-fade">
    <div class="modal-backdrop" @click="close">
      <!-- The dialog swallows its own clicks: nothing inside it may reach the
           backdrop (which closes) or anything further up the page. -->
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        @click.stop
      >
        <!-- Golem fork: the maximize toggle is gone (user call 2026-08-17) —
             modals that need room size themselves by design. The close is
             OURS (user call): a painted ×, not upstream's icon button. -->
        <div class="top-right-buttons">
          <!-- FT-1476: a consumer (the Role Studio drawer) can swap this
               corner for its own controls — Save/Discard while a form
               stands dirty — via the "corner" slot. Nothing passed in
               (every other modal) falls straight through to the default:
               FT-951's close mark (CloseX), the reminder token's own
               hover-to-remove × (icons/x.png), the same mark every close
               control in the app now wears. -->
          <slot name="corner">
            <span class="close-x" title="Close" @click="close">
              <CloseX />
            </span>
          </slot>
        </div>
        <div class="slot">
          <slot />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import CloseX from "../CloseX.vue";

export default {
  components: { CloseX },
  methods: {
    close() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="scss">
// GLOBAL on purpose: the list rule, the size variants and the consumer-class
// selectors all reach into other components' content.
.modal-backdrop {
  position: fixed;
  inset: 0;
  // above App.vue's corner doors (96), below the inline ask panel and the
  // whisper toast (120) and the role hover card (200)
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  // kept under 0.5 — RoleModal's glass samples what sits behind it
  background: rgba(0, 0, 0, 0.3);
}

.modal {
  display: flex;
  flex-direction: column;
  max-width: 80%;
  max-height: 80%;
  padding: 10px 20px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.8);
  box-shadow: 3px 4px 24px rgba(0, 0, 0, 0.9);

  // 80% of a desktop is a comfortable dialog; 80% of a 375px phone throws away
  // 75px of the little width there is, and every modal in this app is a grid
  // of round tokens that then has to shrink to fit. On a small screen the
  // dialog takes the screen.
  @media (max-width: 640px) {
    max-height: 92%;
    max-width: 96%;
    padding: 10px 10px;
  }

  .roles & {
    max-width: 60%;
    max-height: 100%;
    // 60% of a phone is 225px — narrower than the token grid it holds. (This
    // selector outranks the small-screen rule above whatever the media query
    // says, so the override has to live here too.)
    @media (max-width: 640px) {
      max-width: 96%;
    }
  }

  // Every consumer's token grid is a <ul> inside the dialog.
  ul {
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    align-items: center;
    align-content: center;
    margin: 0;
    padding: 0;
    list-style: none;
    line-height: 100%;
  }

  > .top-right-buttons {
    position: absolute;
    // over anything the content stacks (the workbench's panels reach ~40)
    z-index: 60;
    top: 10px;
    right: 16px;
    // Golem fork (FT-951): OUR close — the shared CloseX mark, at the size
    // the old blood letter occupied. CloseX owns the art and the
    // brighten-on-hover glow (src/components/CloseX.vue); this block only
    // sizes and positions it for the modal's corner.
    //
    // NO vertical nudge here. The blood alphabet's X needed `margin-top:
    // -4px` because its drawn crossing sits above the file's centre (the
    // drips hang below it, pulling the visual weight down). icons/x.png is a
    // different file with different geometry — measured, its alpha bounding
    // box is centred within its own square canvas to within half a percent
    // (bbox 86–446 of 539px tall; centre 266 vs canvas centre 269.5), so it
    // needs no compensating offset. Carrying the old nudge over would have
    // pushed this X off-centre for no reason.
    > .close-x {
      cursor: pointer;
      line-height: 1;
      display: block;
      // the painted mark, at the size the typed one occupied
      .close-mark {
        width: 30px;
        height: auto;
      }
    }
  }

  > .slot {
    // opts out of App.vue's global `position: relative`
    position: static;
    max-height: 100%;
  }
}

// Golem fork (FT-854): the Almanac workbench claims the room it needs — the
// class rides the backdrop (component class passthrough) while it is open.
//
// FT-1188: THE CHRONICLE JOINS IT, on the same selector rather than a second
// copy of these six declarations. Both are BIG SURFACES — a page-sized thing
// you go to, standing over the town rather than replacing it — and the whole
// point of the redesign was that they be the same object at the same size, so
// they read the same numbers. A third such surface adds its class here; it
// does not restate the block.
.modal-backdrop.editions.workbench .modal,
.modal-backdrop.records .modal {
  width: 94vw;
  height: 92vh;
  max-width: 94vw;
  max-height: 92vh;
  > .slot {
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
}

// SMALL SCREEN: the workbench does not lay out its bench there — EditionModal
// swaps the body for a short note (its SMALL_BENCH query, kept in step with
// this one). A full-screen shell around one paragraph reads as a page that
// failed to load, so the shell shrinks to what it holds.
@media (max-width: 699px), (pointer: coarse) and (max-height: 519px) {
  .modal-backdrop.editions.workbench .modal {
    height: auto;
    max-height: 92vh;
    // a plate, not a pane: at 0.8 the build panel behind it read straight
    // through the note (the full-screen bench never had anything behind it)
    background: rgba(0, 0, 0, 0.94);
    // A LANDSCAPE PHONE is 345px tall and the note is taller than that, which
    // put its own Close button one pixel below the fold. The slot is the
    // scroller so the whole note — including the way out — is always reachable.
    > .slot {
      height: auto;
      min-height: 0;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
  }
}

// A thumb cannot reliably hit a 30px glyph in a corner. Every modal's close
// gets a real box on a coarse pointer; the glyph inside is unchanged, so the
// corner still reads as one ×.
@media (pointer: coarse) {
  .modal > .top-right-buttons {
    top: 2px;
    right: 6px;
    > .close-x {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      font-size: 34px;
    }
  }
}

// The frame fades in and out.
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 180ms ease-in-out;
}
</style>
